const SELECTORS = {
    searchSortBy: Utility.DomSelector.$$('#sortby-search [name="sort_by"]')
};

const SEARCH_COUNT_PLACEHOLDER = '999999';
const SEARCH_RESULT_TYPES = ['product', 'article', 'page', 'collection'];
const SEARCH_COUNT_CACHE_PREFIX = 'watkins-search-count:';

class TemplateSearchJS {
    constructor() {
      if (SELECTORS.searchSortBy) {
        SELECTORS.searchSortBy.forEach((input) => {
            input.addEventListener('change', (_event) => {
                const selectedRadio = Utility.DomSelector.$('#sortby-search [name="sort_by"]:checked');
                if (!selectedRadio) return;

                const currentParams = new URLSearchParams(window.location.search);
                currentParams.set('sort_by', selectedRadio.value);

                window.location.href = `${window.location.pathname}?${currentParams.toString()}`;
            });
        });
      }

      this.updateSearchResultsCount();
    }

    formatCount(count, oneTemplate, otherTemplate) {
      const template = count === 1 ? oneTemplate : otherTemplate;
      if (!template) return String(count);
      return template.replace(SEARCH_COUNT_PLACEHOLDER, String(count));
    }

    formatSearchCount(count, container) {
      return this.formatCount(
        count,
        container.dataset.searchCountOne,
        container.dataset.searchCountOther
      );
    }

    formatBreakdown(counts, container) {
      const parts = [];

      if (counts.product > 0) {
        parts.push(this.formatCount(
          counts.product,
          container.dataset.searchProductsOne,
          container.dataset.searchProductsOther
        ));
      }

      if (counts.article > 0) {
        parts.push(this.formatCount(
          counts.article,
          container.dataset.searchArticlesOne,
          container.dataset.searchArticlesOther
        ));
      }

      if (counts.page > 0) {
        parts.push(this.formatCount(
          counts.page,
          container.dataset.searchPagesOne,
          container.dataset.searchPagesOther
        ));
      }

      if (counts.collection > 0) {
        parts.push(this.formatCount(
          counts.collection,
          container.dataset.searchCollectionsOne,
          container.dataset.searchCollectionsOther
        ));
      }

      return parts.join(', ');
    }

    emptyCounts() {
      return { product: 0, article: 0, page: 0, collection: 0 };
    }

    countsFromContainer(el) {
      if (!el || el.dataset.searchCountReady !== 'true') return null;

      const counts = {
        product: parseInt(el.dataset.searchTotalProducts || '0', 10) || 0,
        article: parseInt(el.dataset.searchTotalArticles || '0', 10) || 0,
        page: parseInt(el.dataset.searchTotalPagesCount || '0', 10) || 0,
        collection: parseInt(el.dataset.searchTotalCollections || '0', 10) || 0
      };
      const total = SEARCH_RESULT_TYPES.reduce((sum, type) => sum + counts[type], 0);
      const reportedTotal = parseInt(el.dataset.searchTotalItems || '0', 10) || 0;

      if (total <= 0 && reportedTotal <= 0) return null;
      return counts;
    }

    countCacheKey(params) {
      const keyParams = new URLSearchParams(params);
      keyParams.delete('page');
      return `${SEARCH_COUNT_CACHE_PREFIX}${window.location.pathname}?${keyParams.toString()}`;
    }

    readCachedCounts(params) {
      try {
        const raw = sessionStorage.getItem(this.countCacheKey(params));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return {
          product: parseInt(parsed.product, 10) || 0,
          article: parseInt(parsed.article, 10) || 0,
          page: parseInt(parsed.page, 10) || 0,
          collection: parseInt(parsed.collection, 10) || 0
        };
      } catch (_error) {
        return null;
      }
    }

    writeCachedCounts(params, counts) {
      try {
        sessionStorage.setItem(this.countCacheKey(params), JSON.stringify(counts));
      } catch (_error) {
        // Ignore quota / private mode errors.
      }
    }

    countItemsByType(root) {
      const counts = this.emptyCounts();
      const scope = root.querySelector('[data-results-grid]') || root;

      scope.querySelectorAll('[data-search-result-item][data-search-result-type]').forEach((item) => {
        const type = item.getAttribute('data-search-result-type');
        if (SEARCH_RESULT_TYPES.includes(type)) {
          counts[type] += 1;
        }
      });

      return counts;
    }

    setSearchCountLabels(container, counts) {
      const total = SEARCH_RESULT_TYPES.reduce((sum, type) => sum + counts[type], 0);
      if (total <= 0) return;

      const totalText = this.formatSearchCount(total, container);
      const breakdownText = this.formatBreakdown(counts, container);

      const totalEl = container.querySelector('[data-search-results-total]');
      const breakdownEl = container.querySelector('[data-search-results-breakdown]');
      const label = container.querySelector('[data-search-results-count]');
      const topbar = container.querySelector('[data-total-products] p');

      if (totalEl) {
        totalEl.textContent = totalText;
      } else if (label) {
        label.textContent = breakdownText
          ? `${totalText} (${breakdownText})`
          : totalText;
      }

      if (breakdownEl) {
        breakdownEl.textContent = breakdownText ? ` (${breakdownText})` : '';
      }

      if (topbar) {
        topbar.textContent = breakdownText
          ? `${totalText} (${breakdownText})`
          : totalText;
      }

      container.dataset.searchCountReady = 'true';
      container.dataset.searchTotalItems = String(total);
      container.dataset.searchTotalProducts = String(counts.product);
      container.dataset.searchTotalArticles = String(counts.article);
      container.dataset.searchTotalPagesCount = String(counts.page);
      container.dataset.searchTotalCollections = String(counts.collection);
    }

    async fetchPageOneCounts(sectionId, params) {
      const page1Params = new URLSearchParams(params);
      page1Params.delete('page');

      const response = await fetch(
        `${window.location.pathname}?${page1Params.toString()}&section_id=${encodeURIComponent(sectionId)}`
      );
      if (!response.ok) return null;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const page1Container = doc.querySelector('[data-search-container]');
      return this.countsFromContainer(page1Container);
    }

    async sumCountsAcrossPages(container, grid, sectionId, params, totalPages, currentPage) {
      const counts = this.emptyCounts();

      for (let page = 1; page <= totalPages; page += 1) {
        let pageCounts;

        if (page === currentPage) {
          pageCounts = this.countItemsByType(grid);
        } else {
          const pageParams = new URLSearchParams(params);
          if (page <= 1) {
            pageParams.delete('page');
          } else {
            pageParams.set('page', String(page));
          }

          const response = await fetch(
            `${window.location.pathname}?${pageParams.toString()}&section_id=${encodeURIComponent(sectionId)}`
          );
          if (!response.ok) return null;

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          pageCounts = this.countItemsByType(doc);
        }

        SEARCH_RESULT_TYPES.forEach((type) => {
          counts[type] += pageCounts[type];
        });
      }

      return counts;
    }

    /**
     * Keep the same all-pages total on every pagination page as page 1.
     * Page 1: Liquid count (data-search-count-ready). Later pages: cache or page-1 fetch.
     */
    async updateSearchResultsCount() {
      const container = Utility.DomSelector.$('[data-search-container]');
      if (!container) return;

      const sectionId = container.dataset.section;
      const grid = container.querySelector('[data-results-grid]');
      if (!sectionId || !grid) return;

      const totalPages = Math.max(1, parseInt(container.dataset.searchTotalPages || '1', 10));
      const params = new URLSearchParams(window.location.search);
      const currentPage = Math.max(1, parseInt(params.get('page') || '1', 10));

      // Page 1 already has the exact Liquid total — cache it for page 2+.
      const readyCounts = this.countsFromContainer(container);
      if (readyCounts) {
        this.writeCachedCounts(params, readyCounts);
        return;
      }

      // Prefer cached page-1 totals so every pagination page matches instantly.
      const cachedCounts = this.readCachedCounts(params);
      if (cachedCounts) {
        this.setSearchCountLabels(container, cachedCounts);
        return;
      }

      // Single UI page: current-page cards are the full set.
      if (totalPages <= 1) {
        const pageCounts = this.countItemsByType(grid);
        this.setSearchCountLabels(container, pageCounts);
        this.writeCachedCounts(params, pageCounts);
        return;
      }

      // Page 2+: reuse page 1's Liquid totals (same number as the first page).
      try {
        const page1Counts = await this.fetchPageOneCounts(sectionId, params);
        if (page1Counts) {
          this.setSearchCountLabels(container, page1Counts);
          this.writeCachedCounts(params, page1Counts);
          return;
        }

        // Fallback when page 1 could not provide a full count (>250 Shopify results).
        const summedCounts = await this.sumCountsAcrossPages(
          container,
          grid,
          sectionId,
          params,
          totalPages,
          currentPage
        );
        if (summedCounts) {
          this.setSearchCountLabels(container, summedCounts);
          this.writeCachedCounts(params, summedCounts);
        }
      } catch (_error) {
        // Leave provisional Liquid labels in place.
      }
    }
}

new TemplateSearchJS();
